import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { resolvePath } from 'react-router-dom';

import { pages } from 'pages';
import { Header } from 'shared/components/Header/Header';
import { useGetFAQs } from 'api/faqs';
import filterVPPFAQs from 'modules/vpp/utils/filterVPPFAQs';
import VPPIcon from 'shared/components/icons/VPPIcon';
import { useTheme } from 'styled-components';
import { EnvelopeWithMoneyIcon } from 'shared/components/icons/EnvelopeWithMoneyIcon';
import { FAQBody, FAQsWrapper, FAQTitle, IconContainer, LinkStyled } from './VPPFAQs.styles';

export function VPPFAQs() {
  const { t } = useTranslation();
  const theme = useTheme();

  const { data: faqData } = useGetFAQs();

  const getIcon = useCallback(
    (categoryTag: string) => {
      switch (categoryTag) {
        case 'vpp':
          return <VPPIcon color={theme.colors.iconIconPrimarySecondary1100} />;
        case 'vpp_benefits':
          return <EnvelopeWithMoneyIcon color={theme.colors.iconIconPrimarySecondary1100} />;
        default:
          return null;
      }
    },
    [theme.colors.iconIconPrimarySecondary1100],
  );

  const faqItems = useMemo(() => {
    const filteredVPPFAQs = filterVPPFAQs(faqData || null);

    if (!filteredVPPFAQs) {
      return [];
    }

    return Object.entries(filteredVPPFAQs).map(([key, faqs]) => ({
      title: t(`vppFAQs.categories.${key}`),
      faqs,
      icon: getIcon(key),
    }));
  }, [faqData, getIcon, t]);

  return (
    <div>
      <Header
        pageName="VPP_FAQS"
        title={t('vppFAQs.virtualPowerPlantFAQs')}
        routeBreadCrumbs={[
          {
            label: 'Virtual Power Plant',
            link: resolvePath(pages.VPP, `/${pages.DASHBOARD}`),
          },
          { label: 'VPP FAQs' },
        ]}
      />

      <FAQsWrapper>
        {faqItems.map((faqItem) => {
          if (!faqItem.faqs) {
            return null;
          }

          return (
            <div key={faqItem.title}>
              <IconContainer>{faqItem.icon}</IconContainer>
              <FAQTitle as="h3">{faqItem.title}</FAQTitle>
              <FAQBody>
                {faqItem.faqs.map((faq) => (
                  <LinkStyled key={faq.id} to={faq.id}>
                    {faq.question}
                  </LinkStyled>
                ))}
              </FAQBody>
            </div>
          );
        })}
      </FAQsWrapper>
    </div>
  );
}

export default VPPFAQs;
